import { useReducer, useCallback, createContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { defaultState, pageReducer } from './reducers';
import { PageAction, UsePageState } from './types';
import { DEFAULT_FIRST_PAGE, NavigationDirection, PageTypes, PageViewType } from '../../types';
import { isLastPage } from '../../util/paging-util';

export const getDefaultPageState = (): UsePageState => defaultState;

type PageProviderProps = {
  children: React.ReactNode;
};

export const PageContext = createContext<any>([]);

export const PageProvider: React.FC<PageProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer<React.Reducer<UsePageState, PageAction>>(
    pageReducer,
    getDefaultPageState()
  );

  const { pathname } = useLocation();

  useEffect(() => {
    resetZoom();
    resetNoOfPages();
  }, [pathname]);

  const setFirstPage = useCallback(
    (firstPage: number) => {
      dispatch({
        type: 'SET_DEFAULT_FIRST_PAGE',
        payload: { firstPage },
      });
    },
    [dispatch]
  );

  const getCurrentPageData = (
    pages: [],
    hasCover = false,
    comparatorFn?: (page: any, pageNumber: number) => boolean
  ) => {
    return hasCover
      ? [pages?.[DEFAULT_FIRST_PAGE[PageTypes.Pdf]]]
      : state.noOfPages === PageViewType.OnePage
      ? getOnePageData(pages, state.currentPage, comparatorFn)
      : getTwoPageData(pages, state.currentPage, comparatorFn);
  };

  const getOnePageData = (
    pages: any[],
    pageNumber: number,
    comparatorFn: (page: any, pageNumber: number) => boolean = (page) =>
      page.pageNumber === pageNumber
  ) => {
    const requestedPage = pages?.find((page) => comparatorFn(page, pageNumber));
    return requestedPage ? [requestedPage] : [];
  };

  const getTwoPageData = (
    pages: [],
    pageNumber: number,
    comparatorFn?: (page: any, pageNumber: number) => boolean
  ) => {
    const arr = getOnePageData(pages, pageNumber, comparatorFn);
    if (!isLastPage(state.currentPage, state.totalPages, state.firstPage)) {
      return arr.concat(getOnePageData(pages, pageNumber + 1, comparatorFn));
    }
    return arr;
  };

  const setShowCover = useCallback(
    (showCover: any) => {
      dispatch({
        type: 'SET_SHOW_COVER',
        payload: { showCover },
      });
    },
    [dispatch, state]
  );

  const resetZoom = useCallback(() => {
    dispatch({ type: 'RESET_ZOOM' });
  }, [dispatch]);

  const resetNoOfPages = useCallback(() => {
    dispatch({ type: 'RESET_NO_OF_PAGES' });
  }, [dispatch]);

  const resetPage = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
  }, [dispatch, state]);

  const moveToPreviousPage = useCallback(() => {
    const derivedCurrentPage = state.currentPage - state.noOfPages;
    const showCover = derivedCurrentPage < state.firstPage ? true : false;

    const currentPage = derivedCurrentPage < state.firstPage ? state.firstPage : derivedCurrentPage;

    dispatch({
      type: 'GO_LEFT',
      payload: { currentPage, showCover },
    });
  }, [dispatch, state]);

  const moveToNextPage = useCallback(
    (isCoverPage = false) => {
      const pageIncrement = isCoverPage ? PageViewType.OnePage : state.noOfPages;

      dispatch({
        type: 'GO_RIGHT',
        payload: { currentPage: state.currentPage + pageIncrement },
      });
    },
    [dispatch, state]
  );

  const goToPage = useCallback(
    (currentPage: number) => {
      dispatch({
        type: 'GO_TO_PAGE',
        payload: {
          currentPage:
            state.noOfPages === PageViewType.OnePage
              ? currentPage
              : currentPage - (currentPage % 2),
        },
      });
    },
    [dispatch, state]
  );

  const handlePageTurn = useCallback(
    (direction, isCoverPage = false) => {
      direction === NavigationDirection.Next ? moveToNextPage(isCoverPage) : moveToPreviousPage();
    },
    [dispatch, state]
  );

  const setNumberOfPagesDisplayed = useCallback(
    (noOfPages: number) => {
      dispatch({ type: 'SET_NO_OF_PAGES_DISPLAYED', payload: { noOfPages } });
    },
    [dispatch, state]
  );

  const setPageContent = useCallback(
    (contentType: string) => {
      dispatch({ type: 'SET_PAGE_CONTENT', payload: { contentType } });
    },
    [dispatch, state]
  );

  const setPageZoom = useCallback(
    (zoomed: boolean) => {
      dispatch({ type: 'SET_PAGE_ZOOM', payload: { zoomed } });
    },
    [dispatch, state]
  );

  const setNumberOfTotalPages = useCallback(
    (totalPages: number) => {
      dispatch({ type: 'SET_NO_OF_TOTAL_PAGES', payload: { totalPages } });
    },
    [dispatch, state]
  );

  const resetCurrentPageNumber = useCallback(
    () => dispatch({ type: 'RESET_CURRENT_PAGE_NO' }),
    [dispatch, state]
  );

  const setLockForOnePageView = useCallback(
    (lockForOnePageView: boolean) =>
      dispatch({ type: 'SET_LOCK_FOR_ONE_PAGE_VIEW', payload: { lockForOnePageView } }),
    [dispatch, state]
  );

  return (
    <PageContext.Provider
      value={[
        state,
        {
          setNumberOfTotalPages,
          moveToPreviousPage,
          moveToNextPage,
          goToPage,
          setNumberOfPagesDisplayed,
          setPageContent,
          setPageZoom,
          handlePageTurn,
          resetCurrentPageNumber,
          getCurrentPageData,
          setShowCover,
          resetPage,
          setFirstPage,
          setLockForOnePageView,
        },
      ]}
    >
      {children}
    </PageContext.Provider>
  );
};

export default PageProvider;
